import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
} from '@roger/ui-components';
import { FileText, Pencil } from 'lucide-react';
import { PhoneLink } from '../../PhoneLink';

// Patient Notes Component
export const PatientNotes: React.FC<{
  notes: string;
  patientName: string;
  phoneNumber: string;
  onSave: (notes: string) => void;
}> = ({ notes, patientName, phoneNumber, onSave }) => {
  const [noteText, setNoteText] = useState(notes);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempNoteText, setTempNoteText] = useState(notes);

  // Track the saved note when the `notes` prop changes.
  //
  // This deliberately does NOT touch the draft (tempNoteText).
  // Resetting it here wiped whatever the user had typed whenever an intake
  // refresh landed mid-edit; handleOpenModal reseeds the draft from noteText
  // on every open, so there is nothing for this effect to do.
  //
  // The dependency list is also deliberately just [notes]. Adding isModalOpen
  // would re-run this on CLOSE, and Save closes the dialog while the caller's
  // PUT is still in flight — so `notes` is still the pre-save value and
  // setNoteText(notes) would revert the note the user just saved, silently and
  // permanently if that PUT then fails.
  useEffect(() => {
    setNoteText(notes);
  }, [notes]);

  const handleOpenModal = () => {
    setTempNoteText(noteText);
    setIsModalOpen(true);
  };

  const handleSave = () => {
    setNoteText(tempNoteText);
    onSave(tempNoteText);
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setTempNoteText(noteText);
    setIsModalOpen(false);
  };

  return (
    <>
      <div className='group relative'>
        <div
          onClick={handleOpenModal}
          className='flex items-center gap-1.5 px-2 py-1 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 transition-colors cursor-pointer'
        >
          <FileText className='h-3.5 w-3.5 text-gray-700 flex-shrink-0' />
          <span className='text-xs text-gray-600 font-medium'>Notes</span>
          <Pencil className='h-3 w-3 text-gray-600 flex-shrink-0' />

          {/* Tooltip on hover for full text */}
          {noteText && (
            <div className='absolute left-0 top-full mt-1 w-80 p-3 bg-gray-900 text-white text-xs rounded-md shadow-lg z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none'>
              <div className='font-semibold mb-1.5 text-gray-300'>
                Patient Notes:
              </div>
              <div className='leading-relaxed'>{noteText}</div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className='sm:max-w-[500px]'>
          <DialogHeader>
            <DialogTitle className='text-left'>
              <div className='flex flex-col gap-1'>
                <span className='text-lg font-semibold'>{patientName}</span>
                <span className='text-sm font-normal text-muted-foreground'>
                  <PhoneLink phone={phoneNumber} />
                </span>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className='py-4'>
            <textarea
              value={tempNoteText}
              onChange={e => setTempNoteText(e.target.value)}
              className='w-full min-h-[150px] px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y'
              placeholder='Add patient notes...'
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
